import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { updatePracticePatient } from 'api/Patients';
import {
  createSupervisingProvider,
  searchProvider,
  updateSupervisingProvider
} from 'api/SupervisingProvider';

import { ia, iaRa } from 'lib/helpers/utility';
import { useStates } from 'lib/hooks/queries/states/useStates';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import AGBasicTable from 'components/shared/AGBasicTable/AGBasicTable';
import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Modal from 'components/shared/Modal/Modal';

import SupervisingProviderForm from './SupervisingProviderForm';
import {
  DEFAULT_COL_DEFS,
  GRID_OPTIONS,
  cols,
  onGridReady,
  useSupervisingProviderFormik
} from './lib/helper';

const NewSupervisingProvider = ({
  mode,
  setMode,
  autofillCMS1500,
  setAutofillCMS1500,
  selectedProvider,
  setSelectedProvider,
  showNewSupervisingProvider,
  hideNewSupervisingProvider,
  formatSP,
  patientId,
  className = null
}) => {
  const [loading, setLoading] = useState({
    get: false,
    search: false,
    create: false,
    update: false
  });
  const [searchList, setSearchList] = useState([]);
  const navigate = useNavigate();

  const { data } = useStates();
  const stateList = iaRa(data?.states);

  const onSubmit = async (values) => {
    try {
      setLoading({ ...loading, search: true });

      const result = await searchProvider(navigate, values);

      if (ia(result?.npi)) setSearchList(result?.npi);
    } catch (error) {
      showAlert({ message: 'Something went wrong', color: 'danger' });
    }
    setLoading({ ...loading, search: false });
  };

  const formik = useSupervisingProviderFormik(selectedProvider || {}, onSubmit);

  const handleCloseModal = () => {
    hideNewSupervisingProvider();
    formik.resetForm();
    setSearchList([]);
  };

  const mutateCreateSupervisingProvider = useCustomMutation({
    successMessage: 'Supervising Provider created successfully',
    errorMessage: 'Failed to create Supervising Provider',
    invalidateQueryKey: 'supervisingProviders',
    mutationFn: () =>
      createSupervisingProvider(navigate, { patientId, supervisingProvider: selectedProvider }),
    onSuccess: () => {
      updateAutofillCMS1500();
      handleCloseModal();
      setLoading((prevState) => ({ ...prevState, create: false }));
    }
  });

  const mutateUpdateSupervisingProvider = useCustomMutation({
    successMessage: 'Supervising Provider updated successfully',
    errorMessage: 'Failed to update Supervising Provider',
    invalidateQueryKey: 'supervisingProviders',
    mutationFn: () =>
      updateSupervisingProvider(navigate, {
        supervisingProviderId: selectedProvider?.id,
        columns: { prov_id: selectedProvider?.prov_id }
      }),
    onSuccess: () => {
      updateAutofillCMS1500();
      handleCloseModal();
      setLoading((prevState) => ({ ...prevState, update: false }));
    }
  });

  const updateAutofillCMS1500 = async () => {
    if (autofillCMS1500 !== selectedProvider?.autofillCMS1500) {
      await updatePracticePatient(navigate, {
        user_id: patientId,
        data: { auto_ref_prov: selectedProvider?.autofillCMS1500 }
      });
    }
    setAutofillCMS1500(selectedProvider?.autofillCMS1500);
  };

  const onRowSelect = (value) => {
    const data = value?.data;

    if (mode?.select) {
      selectedProvider(data);
      hideNewSupervisingProvider();
      return;
    }

    setSelectedProvider({ ...data, autofillCMS1500 });
    setMode({ ...mode, read: true });
  };

  return (
    <Modal
      isOpen={showNewSupervisingProvider}
      handleClose={handleCloseModal}
      slideFromRight
      className={className}
      title="Supervising Provider"
      footer={
        mode?.read && (
          <>
            <Button text="Cancel" outlined color="neutral" type="link" onClick={handleCloseModal} />

            {mode?.edit ? (
              <Button
                text="Update"
                loading={loading?.update}
                onClick={() => mutateUpdateSupervisingProvider.mutate()}
              />
            ) : (
              <Button
                text="Create"
                loading={loading?.create}
                onClick={() => mutateCreateSupervisingProvider.mutate()}
              />
            )}
          </>
        )
      }>
      {mode?.read ? (
        <>
          {!mode?.edit && (
            <Button
              icon="arrow-left-linear"
              type="link"
              text="Back"
              onClick={() => {
                setMode({ ...mode, read: false });
                setSelectedProvider(null);
              }}
            />
          )}
          <form onSubmit={formik.handleSubmit}>
            <SupervisingProviderForm
              mode={mode}
              setMode={setMode}
              formatSP={formatSP}
              autofillCMS1500={autofillCMS1500}
              selectedProvider={selectedProvider}
              setSelectedProvider={setSelectedProvider}
              stateList={stateList}
            />
          </form>
        </>
      ) : (
        <>
          <form onSubmit={formik.handleSubmit}>
            <SupervisingProviderForm formik={formik} formatSP={formatSP} />
          </form>
          {ia(searchList) && (
            <div className="!mt-4">
              <AGBasicTable
                rowData={searchList}
                defaultColDef={DEFAULT_COL_DEFS}
                columnDefs={cols}
                gridOptions={GRID_OPTIONS}
                onGridReady={onGridReady}
                onRowClicked={onRowSelect}
              />
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default NewSupervisingProvider;
